import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { agentInstanceService } from "../../../services/agentService";
import { llmService, type LlmProviderOption } from "../../../services/llmService";
import { Button } from '../../ui/Button';
import { Checkbox, SelectField, TextField } from '../../ui/Field';

interface Props {
    agent: any;
}

const AISettingsTab: React.FC<Props> = ({ agent }) => {
    const [settings, setSettings] = useState({
        model: 'GPT-4',
        temperature: 0.7,
        maxTokens: 1000,
        systemPrompt: 'You are a helpful customer support agent...',
        responseStyle: 'professional',
        enableContextMemory: true,
        maxContextLength: 10,
    });
    const [saving, setSaving] = useState(false);

    // LLM provider/model selection (saved to aiSettings.llm)
    const [providers, setProviders] = useState<LlmProviderOption[]>([]);
    const [llmProvider, setLlmProvider] = useState('openai');
    const [llmModel, setLlmModel] = useState('');
    const [customModel, setCustomModel] = useState(false);
    const [llmBaseURL, setLlmBaseURL] = useState('');

    useEffect(() => {
        llmService.getProviders().then((r) => setProviders(r.providers)).catch(() => {});
    }, []);

    useEffect(() => {
        if (!agent) return;
        const ai = agent.aiSettings ?? {};
        const s = ai.settings ?? ai;
        const llm = s.llm ?? ai.llm ?? {};
        if (llm.provider) setLlmProvider(llm.provider);
        if (llm.model) setLlmModel(llm.model);
        if (llm.baseURL) setLlmBaseURL(llm.baseURL);

        setSettings(prev => ({
            ...prev,
            model: s.model ?? prev.model,
            temperature: typeof s.temperature === 'number' ? s.temperature : prev.temperature,
            maxTokens: typeof s.maxOutputTokens === 'number' ? s.maxOutputTokens :
                (typeof s.maxTokens === 'number' ? s.maxTokens : prev.maxTokens),
            systemPrompt: s.systemPrompt ?? prev.systemPrompt,
            responseStyle: s.responseStyle ?? prev.responseStyle,
            enableContextMemory: !!(s.contextMemory?.enabled ?? prev.enableContextMemory),
            maxContextLength: typeof s.contextMemory?.maxTurns === 'number'
                ? s.contextMemory.maxTurns
                : (typeof s.maxContextLength === 'number' ? s.maxContextLength : prev.maxContextLength)
        }));
    }, [agent]);


    const handleSettingChange = (key: string, value: any) => {
        setSettings(prev => ({ ...prev, [key]: value }));
    };

    const validate = () => {
        if (!settings.model) return 'Model is required.';
        if (settings.maxTokens <= 0) return 'Max tokens must be greater than 0.';
        if (settings.temperature < 0 || settings.temperature > 1) return 'Temperature must be between 0 and 1.';
        if (settings.enableContextMemory && (!Number.isInteger(settings.maxContextLength) || settings.maxContextLength < 1)) return 'Max context length must be a positive integer.';
        return null;
    };

    const handleSave = async () => {
        const err = validate();
        if (err) {
            toast.error(err);
            return;
        }


        if (!agent || !agent.id) {
            toast.error('Agent id missing. Cannot save settings.');
            return;
        }


        setSaving(true);
        const payload = {
            schemaVersion: Number(agent?.version) || 1,
            settings: {
                model: settings.model,
                temperature: settings.temperature,
                maxOutputTokens: settings.maxTokens,
                systemPrompt: settings.systemPrompt,
                responseStyle: settings.responseStyle,
                contextMemory: {
                    enabled: settings.enableContextMemory,
                    maxTurns: settings.maxContextLength
                },
                // Per-agent LLM provider/model — consumed by the backend factory
                // (resolveLlmClient). Empty model falls back to the platform default.
                llm: {
                    provider: llmProvider,
                    model: llmModel,
                    ...(llmBaseURL ? { baseURL: llmBaseURL } : {}),
                },
            }
        };


        try {
            await agentInstanceService.updateAgentInstanceAiSettings(encodeURIComponent(agent.id), payload);
            toast.success('AI settings saved successfully.');
        } catch (e: any) {
            console.error('Failed to save AI settings', e);
            const msg = e?.message ?? 'Failed to save settings. Please try again.';
            toast.error(msg);
        } finally {
            setSaving(false);
        }
    };
    const currentProvider = providers.find((p) => p.key === llmProvider);
    const showCustomModel = customModel || !!(currentProvider && currentProvider.models.length === 0);

    return (
        <div className="p-6">
            <h3 className="text-lg font-semibold text-agentic-text-primary mb-6">AI Model Configuration</h3>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-agentic-text-secondary mb-2">Provider</label>
                        <SelectField
                            value={llmProvider}
                            onChange={(e) => { setLlmProvider(e.target.value); setCustomModel(false); setLlmModel(''); }}
                            className="w-full px-3 py-2 border border-agentic-gray-300 rounded-lg focus:ring-2 focus:ring-agentic-primary-blue focus:border-transparent"
                        >
                            {providers.map((p) => <option key={p.key} value={p.key}>{p.title}</option>)}
                        </SelectField>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-agentic-text-secondary mb-2">Model</label>
                        {showCustomModel ? (
                            <TextField
                                type="text"
                                value={llmModel}
                                onChange={(e) => setLlmModel(e.target.value)}
                                placeholder="model id (e.g. openai/gpt-4.1-mini)"
                                className="w-full px-3 py-2 border border-agentic-gray-300 rounded-lg focus:ring-2 focus:ring-agentic-primary-blue focus:border-transparent"
                            />
                        ) : (
                            <SelectField
                                value={llmModel}
                                onChange={(e) => setLlmModel(e.target.value)}
                                className="w-full px-3 py-2 border border-agentic-gray-300 rounded-lg focus:ring-2 focus:ring-agentic-primary-blue focus:border-transparent"
                            >
                                <option value="">Select a model…</option>
                                {(currentProvider?.models ?? []).map((m) => <option key={m} value={m}>{m}</option>)}
                            </SelectField>
                        )}
                        {currentProvider?.allowCustomModel && (currentProvider.models.length > 0) && (
                            <label className="mt-2 flex items-center gap-2 text-sm text-agentic-text-secondary">
                                <Checkbox tone="agentic"
                                   
                                    checked={customModel}
                                    onChange={(e) => setCustomModel(e.target.checked)}
                                    className="h-4 w-4 rounded"
                                />
                                Enter Custom Value
                            </label>
                        )}
                        {currentProvider?.modelHint && (
                            <p className="mt-1 text-xs text-gray-400">{currentProvider.modelHint}</p>
                        )}
                    </div>
                    {currentProvider?.requiresBaseURL && (
                        <div>
                            <label className="block text-sm font-medium text-agentic-text-secondary mb-2">Base URL</label>
                            <TextField
                                type="text"
                                value={llmBaseURL}
                                onChange={(e) => setLlmBaseURL(e.target.value)}
                                placeholder="https://your-endpoint/v1"
                                className="w-full px-3 py-2 border border-agentic-gray-300 rounded-lg focus:ring-2 focus:ring-agentic-primary-blue focus:border-transparent"
                            />
                        </div>
                    )}
                    <div>
                        <label className="block text-sm font-medium text-agentic-text-secondary mb-2">Temperature: {settings.temperature}</label>
                        <input
                            type="range"
                            min="0"
                            max="1"
                            step="0.1"
                            value={settings.temperature}
                            onChange={(e) => handleSettingChange('temperature', parseFloat(e.target.value))}
                            className="w-full"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-agentic-text-secondary mb-2">Max Tokens</label>
                        <TextField
                            type="number"
                            value={settings.maxTokens}
                            onChange={(e) => handleSettingChange('maxTokens', parseInt(e.target.value || '0'))}
                            className="w-full px-3 py-2 border border-agentic-gray-300 rounded-lg focus:ring-2 focus:ring-agentic-primary-blue focus:border-transparent"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-agentic-text-secondary mb-2">Response Style</label>
                        <SelectField
                            value={settings.responseStyle}
                            onChange={(e) => handleSettingChange('responseStyle', e.target.value)}
                            className="w-full px-3 py-2 border border-agentic-gray-300 rounded-lg focus:ring-2 focus:ring-agentic-primary-blue focus:border-transparent"
                        >
                            <option value="professional">Professional</option>
                            <option value="friendly">Friendly</option>
                            <option value="formal">Formal</option>
                            <option value="casual">Casual</option>
                        </SelectField>
                    </div>
                </div>
                <div className="space-y-4">
                    <div className="flex items-center">
                        <Checkbox
                           
                            id="contextMemory"
                            checked={settings.enableContextMemory}
                            onChange={(e) => handleSettingChange('enableContextMemory', e.target.checked)}
                            className="h-4 w-4 text-agentic-primary-blue focus:ring-agentic-primary-blue border-agentic-gray-300 rounded"
                        />
                        <label htmlFor="contextMemory" className="ml-2 block text-sm text-agentic-text-secondary">
                            Enable Context Memory
                        </label>
                    </div>
                    {settings.enableContextMemory && (
                        <div>
                            <label className="block text-sm font-medium text-agentic-text-secondary mb-2">Max Context Length</label>
                            <TextField
                                type="number"
                                value={settings.maxContextLength}
                                onChange={(e) => handleSettingChange('maxContextLength', parseInt(e.target.value || '0'))}
                                className="w-full px-3 py-2 border border-agentic-gray-300 rounded-lg focus:ring-2 focus:ring-agentic-primary-blue focus:border-transparent"
                            />
                        </div>
                    )}
                </div>
            </div>
            <div className="mt-6 flex justify-end">
                <Button tone="custom"
                    onClick={handleSave}
                    disabled={saving}
                    className={`px-6 py-2 text-white rounded-lg transition-colors ${saving ? 'bg-gray-400 cursor-not-allowed' : 'bg-agentic-primary-blue hover:bg-agentic-primary-blue/90'}`}
                >
                    {saving ? 'Saving...' : 'Save Settings'}
                </Button>
            </div>
        </div>
    );
};

export default AISettingsTab;
